import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../auth/auth.service';

interface NavChild {
  label: string;
  icon: string;
  route: string;
  module?: string;
  exact?: boolean;
}

interface NavItem {
  label: string;
  icon: string;
  route?: string;
  children?: NavChild[];
  expanded?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule],
  template: `
    <!-- Overlay backdrop (mobile) -->
    @if (mobileOpen) {
      <div class="sidebar-overlay" (click)="closeMobile.emit()"></div>
    }

    <!-- Sidebar -->
    <aside
      class="sidebar"
      [class.collapsed]="collapsed"
      [class.mobile-open]="mobileOpen"
    >
      <div class="sidebar__inner">
        <!-- Logo -->
        <div class="sidebar__header">
          <img
            src="images/logo-panel.svg"
            alt="Peru Money"
            class="sidebar__logo"
            [class.hidden]="collapsed"
          />
          <span class="sidebar__logo-icon" [class.hidden]="!collapsed">PM</span>
          <!-- Mobile close button -->
          <button class="sidebar__close" (click)="closeMobile.emit()">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <!-- Navigation -->
        <nav class="sidebar__nav">
          @for (item of visibleItems; track item.label) {
            @if (item.children) {
              <!-- Group with submenu -->
              <div
                class="sidebar__group"
                [class.sidebar__group--expanded]="item.expanded"
              >
                <button
                  class="sidebar__link sidebar__link--parent"
                  (click)="toggleGroup(item)"
                  [pTooltip]="collapsed ? item.label : ''"
                  tooltipPosition="right"
                >
                  <i [class]="'pi ' + item.icon"></i>
                  <span class="sidebar__label">{{ item.label }}</span>
                  <i
                    class="pi pi-chevron-down sidebar__chevron sidebar__label"
                  ></i>
                </button>
                @if (item.expanded && !collapsed) {
                  <div class="sidebar__submenu">
                    @for (child of item.children; track child.route) {
                      <a
                        class="sidebar__link sidebar__link--child"
                        [routerLink]="child.route"
                        routerLinkActive="sidebar__link--active"
                        [routerLinkActiveOptions]="{
                          exact: child.exact !== false,
                        }"
                        (click)="onNavClick($event, child.route)"
                      >
                        <i [class]="'pi ' + child.icon"></i>
                        <span class="sidebar__label">{{ child.label }}</span>
                      </a>
                    }
                  </div>
                }
              </div>
            } @else {
              <!-- Single link -->
              <a
                class="sidebar__link"
                [routerLink]="item.route"
                routerLinkActive="sidebar__link--active"
                [routerLinkActiveOptions]="{
                  exact: item.route === '/dashboard',
                }"
                [pTooltip]="collapsed ? item.label : ''"
                tooltipPosition="right"
                (click)="onNavClick($event, item.route!)"
              >
                <i [class]="'pi ' + item.icon"></i>
                <span class="sidebar__label">{{ item.label }}</span>
              </a>
            }
          }
        </nav>

        <!-- Toggle (desktop only) -->
        <div class="sidebar__footer">
          <button class="sidebar__toggle" (click)="toggleCollapse.emit()">
            <i
              [class]="collapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'"
            ></i>
            <span class="sidebar__label">Colapsar</span>
          </button>
        </div>
      </div>
    </aside>
  `,
  styles: [
    `
      /* ===== DESKTOP SIDEBAR ===== */
      .sidebar {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        width: 272px;
        z-index: 100;
        transition: width 0.25s ease;
      }
      .sidebar.collapsed {
        width: 72px;
      }

      .sidebar__inner {
        height: 100%;
        background: #000000;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .sidebar__header {
        padding: 1.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 64px;
        border-bottom: 2px solid #ef7c0a;
        position: relative;
      }
      .sidebar__logo {
        width: 150px;
        height: auto;
        transition: opacity 0.2s;
      }
      .sidebar__logo-icon {
        font-weight: 800;
        font-size: 1.25rem;
        color: #25d07b;
        letter-spacing: -0.02em;
      }
      .hidden {
        display: none !important;
      }

      .sidebar__close {
        display: none;
      }

      .sidebar__nav {
        flex: 1;
        padding: 0.75rem;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .sidebar__link {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.85rem 1rem;
        border-radius: 8px;
        color: rgba(255, 255, 255, 0.65);
        text-decoration: none;
        font-size: 1rem;
        font-weight: 500;
        transition: all 0.15s ease;
        white-space: nowrap;
        width: 100%;
        border: none;
        background: none;
        cursor: pointer;
      }
      .sidebar__link i {
        font-size: 1.125rem;
        width: 1.25rem;
        text-align: center;
        flex-shrink: 0;
      }
      .sidebar__link:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #ffffff;
        transform: translateX(4px);
      }
      .sidebar__link--active {
        background: rgba(37, 208, 123, 0.15) !important;
        color: #25d07b !important;
        position: relative;
      }
      .sidebar__link--active::before {
        content: '';
        position: absolute;
        left: 0;
        top: 15%;
        bottom: 15%;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: #25d07b;
        animation: activeBar 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      }
      @keyframes activeBar {
        from {
          transform: scaleY(0);
        }
        to {
          transform: scaleY(1);
        }
      }
      .sidebar__link--active i {
        color: #25d07b;
      }

      /* Parent group */
      .sidebar__link--parent {
        color: rgba(255, 255, 255, 0.45);
        font-size: 0.8125rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 0.6rem 1rem;
        margin-top: 0.5rem;
      }
      .sidebar__link--parent:hover {
        background: transparent;
        color: rgba(255, 255, 255, 0.65);
      }
      .sidebar__chevron {
        margin-left: auto;
        font-size: 0.7rem;
        transition: transform 0.2s ease;
      }
      .sidebar__group--expanded .sidebar__chevron {
        transform: rotate(180deg);
      }

      /* Submenu children */
      .sidebar__submenu {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding-left: 0.5rem;
      }
      .sidebar__link--child {
        font-size: 0.9375rem;
        padding: 0.75rem 1rem;
        color: rgba(255, 255, 255, 0.6);
      }

      .sidebar__label {
        overflow: hidden;
        white-space: nowrap;
        transition:
          opacity 0.2s,
          width 0.2s;
      }
      .collapsed .sidebar__label {
        opacity: 0;
        width: 0;
        overflow: hidden;
      }
      .collapsed .sidebar__link {
        justify-content: center;
        padding: 0.7rem;
      }

      .sidebar__footer {
        padding: 0.75rem;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      }
      .sidebar__toggle {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        padding: 0.625rem 0.875rem;
        border-radius: 8px;
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.45);
        cursor: pointer;
        font-size: 0.8125rem;
        transition: all 0.15s;
      }
      .sidebar__toggle:hover {
        background: rgba(255, 255, 255, 0.06);
        color: rgba(255, 255, 255, 0.7);
      }
      .collapsed .sidebar__toggle {
        justify-content: center;
        padding: 0.625rem;
      }

      /* ===== OVERLAY (mobile only) ===== */
      .sidebar-overlay {
        display: none;
      }

      /* ===== MOBILE ===== */
      @media (max-width: 991px) {
        .sidebar-overlay {
          display: block;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(2px);
          z-index: 99;
          animation: fadeIn 0.2s ease;
        }

        .sidebar {
          width: 280px !important;
          transform: translateX(-100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: none;
        }
        .sidebar.mobile-open {
          transform: translateX(0);
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
        }

        /* Always show full sidebar on mobile (ignore collapsed) */
        .sidebar.collapsed {
          width: 280px !important;
        }
        .collapsed .sidebar__label {
          opacity: 1;
          width: auto;
        }
        .collapsed .sidebar__link {
          justify-content: flex-start;
          padding: 0.7rem 0.875rem;
        }
        .collapsed .sidebar__logo.hidden {
          display: block !important;
        }
        .collapsed .sidebar__logo-icon {
          display: none !important;
        }

        /* Mobile close button */
        .sidebar__close {
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.875rem;
          cursor: pointer;
        }
        .sidebar__close:active {
          background: rgba(255, 255, 255, 0.2);
        }

        /* Bigger touch targets on mobile */
        .sidebar__link {
          padding: 0.875rem 1rem;
          font-size: 0.9375rem;
          gap: 0.875rem;
          border-radius: 10px;
        }
        .sidebar__link i {
          font-size: 1.25rem;
          width: 1.5rem;
        }

        /* Hide desktop collapse toggle */
        .sidebar__footer {
          display: none;
        }
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `,
  ],
})
export class SidebarComponent {
  @Input() collapsed = false;
  @Input() mobileOpen = false;
  @Output() toggleCollapse = new EventEmitter<void>();
  @Output() closeMobile = new EventEmitter<void>();

  private auth = inject(AuthService);
  private router = inject(Router);

  navItems: NavItem[] = [
    { label: 'Dashboard', icon: 'pi-th-large', route: '/dashboard' },
    { label: 'Perfil', icon: 'pi-user', route: '/profile' },
    {
      label: 'Requerimientos',
      icon: 'pi-clipboard',
      expanded: true,
      children: [
        {
          label: 'Req. Agencias',
          icon: 'pi-map-marker',
          route: '/requirements',
          module: 'requirements',
        },
        {
          label: 'Req. Áreas',
          icon: 'pi-sitemap',
          route: '/area-requirements',
          module: 'area-requirements',
        },
        {
          label: 'Req. Materiales',
          icon: 'pi-box',
          route: '/material-requirements',
          module: 'material-requirements',
        },
      ],
    },
    {
      label: 'Compras',
      icon: 'pi-credit-card',
      expanded: true,
      children: [
        {
          label: 'Órdenes Agencias',
          icon: 'pi-shopping-cart',
          route: '/purchase-orders',
          module: 'purchase-orders',
        },
        {
          label: 'Órdenes Áreas',
          icon: 'pi-objects-column',
          route: '/area-purchase-orders',
          module: 'area-purchase-orders',
        },
        {
          label: 'Proveedores',
          icon: 'pi-building',
          route: '/suppliers',
          module: 'suppliers',
        },
      ],
    },
    {
      label: 'Campo',
      icon: 'pi-compass',
      expanded: true,
      children: [
        {
          label: 'Visitas',
          icon: 'pi-car',
          route: '/visits',
          module: 'visits',
        },
      ],
    },
    {
      label: 'Logística',
      icon: 'pi-send',
      expanded: true,
      children: [
        {
          label: 'Distribuciones',
          icon: 'pi-truck',
          route: '/distributions',
          module: 'distributions',
        },
      ],
    },
    {
      label: 'Gestión Humana',
      icon: 'pi-id-card',
      expanded: true,
      children: [
        {
          label: 'Intra',
          icon: 'pi-key',
          route: '/dashboard-intra',
          module: 'dashboard-intra',
        },
        {
          label: 'Asistencia',
          icon: 'pi-calendar',
          route: '/rrhh',
          module: 'rrhh-attendance',
          exact: false,
        },
      ],
    },
    {
      label: 'Marketing',
      icon: 'pi-megaphone',
      expanded: true,
      children: [
        {
          label: 'Sorteo',
          icon: 'pi-gift',
          route: '/dashboard-sorteo',
          module: 'dashboard-sorteo',
        },
        {
          label: 'Clientes Frecuentes',
          icon: 'pi-users',
          route: '/dashboard-clientes',
          module: 'dashboard-clientes',
        },
        {
          label: 'Almacen',
          icon: 'pi-gift',
          route: '/warehouses',
          module: 'warehouses',
        },
        {
          label: 'Categorias Premio',
          icon: 'pi-gift',
          route: '/reward-categories',
          module: 'reward-categories',
        },
        {
          label: 'Premios',
          icon: 'pi-gift',
          route: '/rewards',
          module: 'rewards',
        },
        {
          label: 'Mi Almacen',
          icon: 'pi-gift',
          route: '/warehouse-rewards',
          module: 'warehouse-rewards',
        },
        {
          label: 'Stock de Agencias',
          icon: 'pi-gift',
          route: '/reward-agencies',
          module: 'reward-agencies',
        },
        {
          label: 'Clientes',
          icon: 'pi-users',
          route: '/customers',
          module: 'customers',
        },
        {
          label: 'Canjes',
          icon: 'pi-gift',
          route: '/redeems',
          module: 'redeems',
        },
      ],
    },
    {
      label: 'Mantenimiento',
      icon: 'pi-wrench',
      expanded: true,
      children: [
        {
          label: 'Equipos',
          icon: 'pi-server',
          route: '/maintenance/equipment',
          module: 'maintenance-equipment',
        },
        {
          label: 'Mantenimientos',
          icon: 'pi-cog',
          route: '/maintenance/maintenances',
          module: 'maintenance-maintenances',
        },
        {
          label: 'Alertas',
          icon: 'pi-bell',
          route: '/maintenance/alerts',
          module: 'maintenance-alerts',
        },
        {
          label: 'Costos',
          icon: 'pi-chart-bar',
          route: '/maintenance/cost-summary',
          module: 'maintenance-costs',
        },
        {
          label: 'Tipos de Equipo',
          icon: 'pi-list',
          route: '/maintenance/equipment-types',
          module: 'maintenance-types',
        },
      ],
    },
    {
      label: 'Metricas',
      icon: 'pi-chart-bar',
      expanded: true,
      children: [
        {
          label: 'Resumen',
          icon: 'pi-th-large',
          route: '/metrics',
          module: 'metrics-dashboard',
        },
        {
          label: 'Agencias',
          icon: 'pi-building',
          route: '/metrics/agencies',
          module: 'metrics-agencies',
        },
        {
          label: 'Operaciones',
          icon: 'pi-chart-line',
          route: '/metrics/operations',
          module: 'metrics-operations',
        },
        {
          label: 'Western Union',
          icon: 'pi-globe',
          route: '/metrics/western-union',
          module: 'metrics-western-union',
        },
        {
          label: 'Clientes',
          icon: 'pi-users',
          route: '/metrics/clients',
          module: 'metrics-clients',
        },
        {
          label: 'Operadores',
          icon: 'pi-id-card',
          route: '/metrics/operators',
          module: 'metrics-operators',
        },
        {
          label: 'Cambistas',
          icon: 'pi-wallet',
          route: '/metrics/cambistas',
          module: 'metrics-cambistas',
        },
        {
          label: 'Cliente Frecuente',
          icon: 'pi-star',
          route: '/metrics/cliente-frecuente',
          module: 'metrics-cf',
        },
      ],
    },
    {
      label: 'Cripto',
      icon: 'pi-bitcoin',
      expanded: true,
      children: [
        {
          label: 'Dashboard',
          icon: 'pi-th-large',
          route: '/cripto-money',
          module: 'crypto-dashboard',
        },
        {
          label: 'Operaciones',
          icon: 'pi-arrows-h',
          route: '/cripto-money/operations',
          module: 'crypto-operations',
        },
        {
          label: 'Clientes',
          icon: 'pi-users',
          route: '/cripto-money/customers',
          module: 'crypto-customers',
        },
        {
          label: 'Tipo de Cambio',
          icon: 'pi-chart-line',
          route: '/cripto-money/exchange-rates',
          module: 'crypto-exchange-rates',
        },
        {
          label: 'Caja',
          icon: 'pi-wallet',
          route: '/cripto-money/cash-drawer',
          module: 'crypto-cash-drawer',
        },
        {
          label: 'Liquidaciones',
          icon: 'pi-receipt',
          route: '/cripto-money/liquidaciones',
          module: 'crypto-cash-drawer',
        },
        {
          label: 'Billeteras',
          icon: 'pi-server',
          route: '/cripto-money/wallets',
          module: 'crypto-wallets',
        },
        {
          label: 'Monedas',
          icon: 'pi-money-bill',
          route: '/cripto-money/currencies',
          module: 'crypto-currencies',
        },
        {
          label: 'Facturación',
          icon: 'pi-file',
          route: '/cripto-money/billing',
          module: 'crypto-billing',
        },
        {
          label: 'Configuración',
          icon: 'pi-cog',
          route: '/cripto-money/config',
          module: 'crypto-config',
        },
        {
          label: 'Reportes',
          icon: 'pi-chart-bar',
          route: '/cripto-money/reports',
          module: 'crypto-reports',
        },
      ],
    },
    {
      label: 'Conciliaciones',
      icon: 'pi-building-columns',
      expanded: true,
      children: [
        {
          label: 'Dashboard',
          icon: 'pi-th-large',
          route: '/conciliaciones',
          module: 'conciliaciones-dashboard',
        },
        {
          label: 'Carga Archivos',
          icon: 'pi-upload',
          route: '/conciliaciones/uploads',
          module: 'conciliaciones-uploads',
        },
        {
          label: 'Transacciones',
          icon: 'pi-list',
          route: '/conciliaciones/transactions',
          module: 'conciliaciones-transactions',
        },
        {
          label: 'Asignaciones',
          icon: 'pi-users',
          route: '/conciliaciones/assignments',
          module: 'conciliaciones-assignments',
        },
        {
          label: 'Cuentas',
          icon: 'pi-wallet',
          route: '/conciliaciones/accounts',
          module: 'conciliaciones-accounts',
        },
        {
          label: 'Catálogos',
          icon: 'pi-tags',
          route: '/conciliaciones/catalogs',
          module: 'conciliaciones-catalogs',
        },
      ],
    },
    {
      label: 'Configuración',
      icon: 'pi-cog',
      expanded: true,
      children: [
        {
          label: 'Centros de Costo',
          icon: 'pi-wallet',
          route: '/cost-centers',
          module: 'cost-centers',
        },
        {
          label: 'Presupuestos',
          icon: 'pi-chart-bar',
          route: '/budgets',
          module: 'budgets',
        },
        {
          label: 'Usuarios',
          icon: 'pi-users',
          route: '/users',
          module: 'users',
        },
        {
          label: 'Permisos',
          icon: 'pi-lock',
          route: '/role-modules',
          module: 'role-modules',
        },
        {
          label: 'Catálogos',
          icon: 'pi-list',
          route: '/system-catalogs',
          module: 'system-catalogs',
        },
        {
          label: 'Sync Usuarios',
          icon: 'pi-sync',
          route: '/sync-users',
          module: 'sync-users',
        },
        {
          label: 'Sync Agencias',
          icon: 'pi-building',
          route: '/sync-agencies',
          module: 'sync-agencies',
        },
      ],
    },
  ];

  get visibleItems(): NavItem[] {
    return this.navItems
      .map((item) => {
        if (!item.children) return item;
        const visibleChildren = item.children.filter(
          (child) => !child.module || this.auth.hasModule(child.module),
        );
        if (visibleChildren.length === 0) return null;
        return { ...item, children: visibleChildren };
      })
      .filter((item): item is NavItem => item !== null);
  }

  toggleGroup(item: NavItem): void {
    const original = this.navItems.find((n) => n.label === item.label);
    if (original) original.expanded = !original.expanded;
  }

  onNavClick(event: Event, route: string): void {
    if (this.router.url.startsWith(route)) {
      event.preventDefault();
      return;
    }
    this.closeMobile.emit();
  }
}
